import { useEffect, useMemo, useRef, useState, useCallback } from "react"
import { useParams, useNavigate, useSearchParams } from "react-router"
import { jsPDF } from "jspdf"
import PageBreadcrumb from "../../components/common/PageBreadCrumb"
import PageMeta from "../../components/common/PageMeta"
import { captureSvgAsPng } from "../../utils/svgToImage"
import ProposedPlugDiagram, { ProposedPlugWBDHandle } from "../../components/diagrams/ProposedPlugWBD"
import AsPluggedWBD, { AsPluggedWBDHandle } from "../../components/diagrams/AsPluggedWBD"
import { createManualWBD, updateManualWBD, getManualWBD } from "../../lib/api/manual-wbd"
import { fetchWellComponentsWithGeometry, fetchWellByApi14, getWellWbdSync, type WellComponentsResponse } from "../../lib/api/wells"
import { useToast } from "../../contexts/ToastContext"

// ---------------------------------------------------------------------------
// Local types
// ---------------------------------------------------------------------------

type DiagramType = "current" | "planned" | "as_plugged"
type Jurisdiction = "TX" | "NM"

interface WellHeader {
  api14: string
  operator_name: string
  lease_name: string
  field_name: string
  well_number: string
}

interface CasingRow {
  id: string
  string: string
  size_in: number | ""
  top_ft: number | ""
  bottom_ft: number | ""
  hole_size_in: number | ""
  cement_top_ft: number | ""
}

interface FormationRow {
  id: string
  formation: string
  top_ft: number | ""
  base_ft: number | ""
}

interface PerfRow {
  id: string
  top_ft: number | ""
  bottom_ft: number | ""
}

interface TubingRow {
  id: string
  size_in: number | ""
  top_ft: number | ""
  bottom_ft: number | ""
}

interface ToolRow {
  id: string
  tool_type: string
  depth_ft: number | ""
  top_ft: number | ""
  bottom_ft: number | ""
}

interface CementJobRow {
  id: string
  job_type: string          // "perf_and_squeeze", "perf_and_circulate", "spot_and_dump_bail"
  measured_top_ft: number | ""
  measured_bottom_ft: number | ""
  tagged_top_ft: number | ""
  sacks: number | ""
  through_casing: string    // The casing string name (e.g., "Production") — matches CasingRow.string
  is_new: boolean           // true = new/planned cement, false = existing cement
}

interface StepRow {
  id: string
  type: string           // "cement_plug", "bridge_plug", "perf_and_squeeze", "perf_and_circulate", "surface_plug", "formation_plug"
  plug_type: string      // "spot_plug", "perf_and_squeeze_plug", "balanced_plug", "circulation_plug"
  formation: string
  top_ft: number | ""
  bottom_ft: number | ""
  sacks: number | ""
  cement_class: string
  display_name: string
  plug_purpose: string   // "isolation", "abandonment", "protection"
  through_casing: string // casing string name for annular operations
}

interface PlugRow {
  id: string
  plug_number: number | ""
  type: string           // "cement_plug", "bridge_plug", "perf_and_squeeze", "perf_and_circulate", "surface_plug"
  plug_type: string      // "spot_plug", "perf_and_squeeze_plug", "balanced_plug"
  formation: string
  display_name: string
  top_ft: number | ""
  bottom_ft: number | ""
  sacks: number | ""
  cement_class: string
  tagged_depth_ft: number | ""
  placement_method: string
  through_casing: string // casing string name for annular operations
  woc_hours: number | ""
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

let _uidCounter = 0
function uid(): string {
  return `uid-${Date.now()}-${++_uidCounter}`
}

function emptyCasing(): CasingRow {
  return { id: uid(), string: "Surface", size_in: "", top_ft: 0, bottom_ft: "", hole_size_in: "", cement_top_ft: "" }
}

function emptyFormation(): FormationRow {
  return { id: uid(), formation: "", top_ft: "", base_ft: "" }
}

function emptyPerf(): PerfRow {
  return { id: uid(), top_ft: "", bottom_ft: "" }
}

function emptyTubing(): TubingRow {
  return { id: uid(), size_in: "", top_ft: 0, bottom_ft: "" }
}

function emptyTool(): ToolRow {
  return { id: uid(), tool_type: "CIBP", depth_ft: "", top_ft: "", bottom_ft: "" }
}

function emptyCementJob(): CementJobRow {
  return { id: uid(), job_type: "perf_and_squeeze", measured_top_ft: "", measured_bottom_ft: "", tagged_top_ft: "", sacks: "", through_casing: "", is_new: false }
}

function emptyStep(): StepRow {
  return { id: uid(), type: "cement_plug", plug_type: "", formation: "", top_ft: "", bottom_ft: "", sacks: "", cement_class: "", display_name: "", plug_purpose: "", through_casing: "" }
}

function emptyPlug(plugNumber: number): PlugRow {
  return {
    id: uid(),
    plug_number: plugNumber,
    type: "cement_plug",
    plug_type: "",
    formation: "",
    display_name: "",
    top_ft: "",
    bottom_ft: "",
    sacks: "",
    cement_class: "",
    tagged_depth_ft: "",
    placement_method: "",
    woc_hours: "",
    through_casing: "",
  }
}

// ---------------------------------------------------------------------------
// Reusable micro-components
// ---------------------------------------------------------------------------

function Inp({ className = "", ...props }: React.InputHTMLAttributes<HTMLInputElement> & { className?: string }) {
  return (
    <input
      {...props}
      className={`w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 ${className}`}
    />
  )
}

function Sel({
  className = "",
  children,
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement> & { className?: string }) {
  return (
    <select
      {...props}
      className={`w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 ${className}`}
    >
      {children}
    </select>
  )
}

function Section({
  title,
  children,
  onAdd,
  addLabel = "Add Row",
}: {
  title: string
  children: React.ReactNode
  onAdd: () => void
  addLabel?: string
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-gray-800 dark:text-white">{title}</h3>
        <button
          type="button"
          onClick={onAdd}
          className="px-3 py-1 text-xs rounded-lg bg-brand-500 text-white hover:bg-brand-600 transition-colors"
        >
          + {addLabel}
        </button>
      </div>
      {children}
    </div>
  )
}

function RemoveBtn({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title="Remove row"
      className="px-2 py-1 text-xs rounded bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-800/40 transition-colors"
    >
      ✕
    </button>
  )
}

const TH = ({ children }: { children: React.ReactNode }) => (
  <th className="pb-2 pr-2 text-left text-gray-500 dark:text-gray-400 font-medium">{children}</th>
)

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ManualWBDEditor() {
  const { id } = useParams<{ id?: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { success, error: showError } = useToast()

  const isEditMode = Boolean(id)
  const urlApi14 = searchParams.get("api14")

  // ── Core state ──────────────────────────────────────────────────────────
  const [diagramType, setDiagramType] = useState<DiagramType>("current")
  const [jurisdiction, setJurisdiction] = useState<Jurisdiction>("TX")
  const [title, setTitle] = useState("")
  const [saving, setSaving] = useState(false)
  const [loadingRecord, setLoadingRecord] = useState(false)
  const [loadingWell, setLoadingWell] = useState(false)

  const [wellHeader, setWellHeader] = useState<WellHeader>({
    api14: "",
    operator_name: "",
    lease_name: "",
    field_name: "",
    well_number: "",
  })

  const [casingRows, setCasingRows] = useState<CasingRow[]>([emptyCasing()])
  const [formationRows, setFormationRows] = useState<FormationRow[]>([emptyFormation()])
  const [perfRows, setPerfRows] = useState<PerfRow[]>([emptyPerf()])
  const [tubingRows, setTubingRows] = useState<TubingRow[]>([emptyTubing()])
  const [toolRows, setToolRows] = useState<ToolRow[]>([])
  const [cementJobRows, setCementJobRows] = useState<CementJobRow[]>([])
  const [stepRows, setStepRows] = useState<StepRow[]>([emptyStep()])
  const [plugRows, setPlugRows] = useState<PlugRow[]>([emptyPlug(1)])

  // Visibility toggles for diagram layers
  const [visibility, setVisibility] = useState({
    casing: true,
    tubing: true,
    perforations: true,
    formationTops: true,
    newCementPlugs: true,
    existingCement: true,
    bridgePlugs: true,
    existingTools: true,
  })

  // Diagram refs for SVG export
  const proposedRef = useRef<ProposedPlugWBDHandle>(null)
  const asPluggedRef = useRef<AsPluggedWBDHandle>(null)

  // ── Load existing record in edit mode ──────────────────────────────────
  useEffect(() => {
    if (!id) return
    setLoadingRecord(true)
    getManualWBD(id)
      .then((record) => {
        setTitle(record.title ?? "")
        const dt: DiagramType = record.diagram_type ?? "current"
        setDiagramType(dt)

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const dd = (record.diagram_data ?? {}) as any

        // Well header
        if (dd.well) {
          setWellHeader({
            api14: dd.well.api14 ?? "",
            operator_name: dd.well.operator_name ?? "",
            lease_name: dd.well.lease_name ?? "",
            field_name: dd.well.field_name ?? "",
            well_number: dd.well.well_number ?? "",
          })
        }

        const geom = dd.well_geometry ?? {}

        if (geom.casing_strings?.length) {
          setCasingRows(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            geom.casing_strings.map((c: any) => ({
              id: uid(),
              string: c.string ?? "Surface",
              size_in: c.size_in !== undefined ? Number(c.size_in) : "",
              top_ft: c.top_ft !== undefined ? Number(c.top_ft) : 0,
              bottom_ft: c.bottom_ft !== undefined ? Number(c.bottom_ft) : "",
              hole_size_in: c.hole_size_in !== undefined ? Number(c.hole_size_in) : "",
              cement_top_ft:
                c.cement_top_ft !== undefined && c.cement_top_ft !== null
                  ? Number(c.cement_top_ft)
                  : "",
            }))
          )
        }

        if (geom.formation_tops?.length) {
          setFormationRows(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            geom.formation_tops.map((f: any) => ({
              id: uid(),
              formation: f.formation ?? "",
              top_ft: f.top_ft !== undefined ? Number(f.top_ft) : "",
              base_ft:
                f.base_ft !== undefined && f.base_ft !== null ? Number(f.base_ft) : "",
            }))
          )
        }

        if (geom.production_perforations?.length) {
          setPerfRows(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            geom.production_perforations.map((p: any) => ({
              id: uid(),
              top_ft: p.top_ft !== undefined ? Number(p.top_ft) : "",
              bottom_ft: p.bottom_ft !== undefined ? Number(p.bottom_ft) : "",
            }))
          )
        }

        if (geom.tubing?.length) {
          setTubingRows(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            geom.tubing.map((t: any) => ({
              id: uid(),
              size_in: t.size_in !== undefined ? Number(t.size_in) : "",
              top_ft: t.top_ft !== undefined ? Number(t.top_ft) : 0,
              bottom_ft: t.bottom_ft !== undefined ? Number(t.bottom_ft) : "",
            }))
          )
        }

        if (geom.existing_tools?.length) {
          setToolRows(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            geom.existing_tools.map((t: any) => ({
              id: uid(),
              tool_type: t.tool_type ?? "CIBP",
              depth_ft: t.depth_ft !== undefined ? Number(t.depth_ft) : "",
              top_ft:
                t.top_ft !== undefined && t.top_ft !== null ? Number(t.top_ft) : "",
              bottom_ft:
                t.bottom_ft !== undefined && t.bottom_ft !== null
                  ? Number(t.bottom_ft)
                  : "",
            }))
          )
        }

        if (geom.historic_cement_jobs?.length) {
          setCementJobRows(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            geom.historic_cement_jobs.map((j: any) => ({
              id: uid(),
              job_type: j.job_type ?? "perf_and_squeeze",
              // Support both old field names (interval_top_ft) and new (measured_top_ft)
              measured_top_ft:
                (j.measured_top_ft !== undefined && j.measured_top_ft !== null)
                  ? Number(j.measured_top_ft)
                  : (j.interval_top_ft !== undefined && j.interval_top_ft !== null)
                    ? Number(j.interval_top_ft)
                    : "",
              measured_bottom_ft:
                (j.measured_bottom_ft !== undefined && j.measured_bottom_ft !== null)
                  ? Number(j.measured_bottom_ft)
                  : (j.interval_bottom_ft !== undefined && j.interval_bottom_ft !== null)
                    ? Number(j.interval_bottom_ft)
                    : "",
              tagged_top_ft:
                (j.tagged_top_ft !== undefined && j.tagged_top_ft !== null)
                  ? Number(j.tagged_top_ft)
                  : (j.cement_top_ft !== undefined && j.cement_top_ft !== null)
                    ? Number(j.cement_top_ft)
                    : "",
              sacks:
                j.sacks !== undefined && j.sacks !== null ? Number(j.sacks) : "",
              through_casing: j.through_casing ?? "",
              is_new: j.is_new ?? false,
            }))
          )
        }

        // Steps (planned)
        if (dd.payload?.steps?.length) {
          setStepRows(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            dd.payload.steps.map((s: any) => ({
              id: uid(),
              type: s.type ?? "cement_plug",
              plug_type: s.plug_type ?? "",
              formation: s.formation ?? "",
              top_ft:
                s.top_ft !== undefined && s.top_ft !== null ? Number(s.top_ft) : "",
              bottom_ft: s.bottom_ft !== undefined ? Number(s.bottom_ft) : "",
              sacks:
                s.sacks !== undefined && s.sacks !== null ? Number(s.sacks) : "",
              cement_class: s.recipe?.class ?? "",
              display_name: s.display_name ?? "",
              plug_purpose: s.plug_purpose ?? "",
              through_casing: s.through_casing ?? "",
            }))
          )
        }

        // Plugs (as_plugged)
        if (dd.plugs?.length) {
          setPlugRows(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            dd.plugs.map((p: any, i: number) => ({
              id: uid(),
              plug_number:
                p.plug_number !== undefined ? Number(p.plug_number) : i + 1,
              type: p.type ?? "cement_plug",
              plug_type: p.plug_type ?? "",
              formation: p.formation ?? "",
              display_name: p.display_name ?? "",
              top_ft: p.top_ft !== undefined ? Number(p.top_ft) : "",
              bottom_ft: p.bottom_ft !== undefined ? Number(p.bottom_ft) : "",
              sacks: p.sacks !== undefined ? Number(p.sacks) : "",
              cement_class: p.cement_class ?? "",
              tagged_depth_ft:
                p.tagged_depth_ft !== undefined && p.tagged_depth_ft !== null
                  ? Number(p.tagged_depth_ft)
                  : "",
              placement_method: p.placement_method ?? "",
              woc_hours:
                p.woc_hours !== undefined && p.woc_hours !== null
                  ? Number(p.woc_hours)
                  : "",
              through_casing: p.through_casing ?? "",
            }))
          )
          if (dd.jurisdiction) {
            setJurisdiction(dd.jurisdiction as Jurisdiction)
          }
        }
      })
      .catch((err) => {
        showError("Failed to load record: " + ((err as Error)?.message ?? "unknown error"))
      })
      .finally(() => setLoadingRecord(false))
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load from well (WellComponents + ResearchSession summary merged) ─────
  const handleLoadFromWell = useCallback(async (api14Override?: string) => {
    const api14 = api14Override || wellHeader.api14
    if (!api14) {
      showError("Enter an API14 number first.")
      return
    }
    setLoadingWell(true)
    try {
      // Fire both sources in parallel
      const [wellResp, compResp, summary] = await Promise.all([
        fetchWellByApi14(api14).catch(() => null),
        fetchWellComponentsWithGeometry(api14).catch(() => ({ geometry: null, components: [] })),
        getWellWbdSync(api14).catch(() => null),
      ])

      // ── Well header ──────────────────────────────────────────────────────
      const wi = summary?.well_info ?? {}
      if (wellResp) {
        setWellHeader({
          api14: wellResp.api14 ?? api14,
          operator_name: wellResp.operator_name || wi.operator || "",
          lease_name: wellResp.lease_name || wi.lease || "",
          field_name: wellResp.field_name || wi.field || "",
          well_number: wellResp.well_number || wi.well_no || "",
        })
      } else {
        setWellHeader((prev) => ({
          ...prev,
          api14,
          operator_name: prev.operator_name || wi.operator || prev.operator_name,
          lease_name: prev.lease_name || wi.lease || prev.lease_name,
          field_name: prev.field_name || wi.field || prev.field_name,
          well_number: prev.well_number || wi.well_no || prev.well_number,
        }))
      }

      const geom = compResp.geometry ?? {} as WellComponentsResponse["geometry"]

      // ── Casing: wbd-sync (filed documents) first, fall back to WellComponents ──
      if (summary?.casing_records.length) {
        setCasingRows(
          summary.casing_records.map((c) => ({
            id: uid(),
            string: c.string_type ?? "Surface",
            size_in: c.size_in !== undefined ? Number(c.size_in) : "" as number | "",
            top_ft: c.top_ft !== undefined ? Number(c.top_ft) : 0 as number | "",
            bottom_ft: c.bottom_ft !== undefined ? Number(c.bottom_ft) : "" as number | "",
            hole_size_in: c.hole_size_in !== undefined ? Number(c.hole_size_in) : "" as number | "",
            cement_top_ft: "" as number | "",
          }))
        )
      } else if (geom.casing_strings?.length) {
        setCasingRows(
          geom.casing_strings.map((c) => ({
            id: uid(),
            string: c.string_type ?? "Surface",
            size_in: c.outside_dia_in !== undefined ? Number(c.outside_dia_in) : "" as number | "",
            top_ft: c.top_ft !== undefined ? Number(c.top_ft) : 0 as number | "",
            bottom_ft: c.bottom_ft !== undefined ? Number(c.bottom_ft) : "" as number | "",
            hole_size_in: c.hole_size_in !== undefined ? Number(c.hole_size_in) : "" as number | "",
            cement_top_ft: c.cement_top_ft != null ? Number(c.cement_top_ft) : "" as number | "",
          }))
        )
      }

      // ── Liner — append as casing rows with string="Liner" ────────────────
      if (geom.liner?.length) {
        const linerCasings: CasingRow[] = geom.liner.map((l) => ({
          id: uid(),
          string: "Liner",
          size_in: l.size_in !== undefined ? Number(l.size_in) : "" as number | "",
          top_ft: l.top_ft !== undefined ? Number(l.top_ft) : 0 as number | "",
          bottom_ft: l.bottom_ft !== undefined ? Number(l.bottom_ft) : "" as number | "",
          hole_size_in: "" as number | "",
          cement_top_ft: "" as number | "",
        }))
        setCasingRows(prev => [...prev, ...linerCasings])
      }

      // ── Formation tops ───────────────────────────────────────────────────
      if (geom.formation_tops?.length) {
        setFormationRows(
          geom.formation_tops.map((f) => ({
            id: uid(),
            formation: f.formation ?? "",
            top_ft: f.top_ft !== undefined ? Number(f.top_ft) : "" as number | "",
            base_ft: "" as number | "",
          }))
        )
      }

      // ── Perforations: wbd-sync first, WellComponents fallback ────────────
      if (summary?.perf_records?.length) {
        setPerfRows(
          summary.perf_records.map((p) => ({
            id: uid(),
            top_ft: p.top_ft !== undefined ? Number(p.top_ft) : "" as number | "",
            bottom_ft: p.bottom_ft !== undefined ? Number(p.bottom_ft) : "" as number | "",
          }))
        )
      } else {
        const perfs = geom.production_perforations?.length ? geom.production_perforations : geom.perforations
        if (perfs?.length) {
          setPerfRows(
            perfs.map((p) => ({
              id: uid(),
              top_ft: p.top_ft !== undefined ? Number(p.top_ft) : "" as number | "",
              bottom_ft: p.bottom_ft !== undefined ? Number(p.bottom_ft) : "" as number | "",
            }))
          )
        }
      }

      // ── Tubing: wbd-sync first, WellComponents fallback ──────────────────
      if (summary?.tubing_records?.length) {
        setTubingRows(
          summary.tubing_records.map((t) => ({
            id: uid(),
            size_in: t.size_in !== undefined ? Number(t.size_in) : "" as number | "",
            top_ft: t.top_ft !== undefined && t.top_ft !== null ? Number(t.top_ft) : 0 as number | "",
            bottom_ft: t.bottom_ft !== undefined ? Number(t.bottom_ft) : "" as number | "",
          }))
        )
      } else if (geom.tubing?.length) {
        setTubingRows(
          geom.tubing.map((t) => ({
            id: uid(),
            size_in: t.size_in !== undefined ? Number(t.size_in) : "" as number | "",
            top_ft: t.top_ft !== undefined ? Number(t.top_ft) : 0 as number | "",
            bottom_ft: t.bottom_ft !== undefined ? Number(t.bottom_ft) : "" as number | "",
          }))
        )
      }

      // ── Tools: wbd-sync first, WellComponents fallback ───────────────────
      if (summary?.tool_records?.length) {
        setToolRows(
          summary.tool_records.map((t) => ({
            id: uid(),
            tool_type: t.tool_type ?? "retainer",
            depth_ft: t.depth_ft !== undefined ? Number(t.depth_ft) : "" as number | "",
            top_ft: "" as number | "",
            bottom_ft: "" as number | "",
          }))
        )
      } else {
        const tools = [...(geom.existing_tools ?? []), ...(geom.mechanical_equipment ?? [])]
        if (tools.length) {
          setToolRows(
            tools.map((t) => ({
              id: uid(),
              tool_type: t.tool_type ?? ("type" in t ? (t as { type?: string }).type : undefined) ?? "CIBP",
              depth_ft: t.depth_ft !== undefined ? Number(t.depth_ft) : "" as number | "",
              top_ft: t.top_ft != null ? Number(t.top_ft) : "" as number | "",
              bottom_ft: t.bottom_ft != null ? Number(t.bottom_ft) : "" as number | "",
            }))
          )
        }
      }

      // ── Historic cement jobs: wbd-sync first, WellComponents fallback ─────
      if (summary?.cement_job_records?.length) {
        setCementJobRows(
          summary.cement_job_records.map((j) => ({
            id: uid(),
            job_type: j.job_type ?? "perf_and_squeeze",
            measured_top_ft: j.interval_top_ft !== undefined && j.interval_top_ft !== null ? Number(j.interval_top_ft) : "" as number | "",
            measured_bottom_ft: j.interval_bottom_ft !== undefined ? Number(j.interval_bottom_ft) : "" as number | "",
            tagged_top_ft: j.cement_top_ft !== undefined && j.cement_top_ft !== null ? Number(j.cement_top_ft) : "" as number | "",
            sacks: j.sacks !== undefined ? Number(j.sacks) : "" as number | "",
            through_casing: "",
            is_new: false,
          }))
        )
      } else if (geom.historic_cement_jobs?.length) {
        setCementJobRows(
          geom.historic_cement_jobs.map((j) => ({
            id: uid(),
            job_type: j.job_type ?? "perf_and_squeeze",
            measured_top_ft: j.interval_top_ft != null ? Number(j.interval_top_ft) : "" as number | "",
            measured_bottom_ft: j.interval_bottom_ft != null ? Number(j.interval_bottom_ft) : "" as number | "",
            tagged_top_ft: j.cement_top_ft != null ? Number(j.cement_top_ft) : "" as number | "",
            sacks: j.sacks != null ? Number(j.sacks) : "" as number | "",
            through_casing: "",
            is_new: false,
          }))
        )
      }

      // ── Plugs from ResearchSession (document-extracted) ──────────────────
      if (summary?.plug_records.length) {
        const mapPlugType = (pt?: string): string => {
          if (!pt) return "cement_plug"
          if (pt === "bridge_plug" || pt === "mechanical_plug") return "bridge_plug"
          if (pt === "perf_and_squeeze") return "perf_and_squeeze"
          return "cement_plug"
        }

        if (diagramType === "as_plugged") {
          setPlugRows(
            summary.plug_records.map((p, i) => ({
              id: uid(),
              plug_number: p.plug_number !== undefined ? Number(p.plug_number) : (i + 1) as number | "",
              type: mapPlugType(p.plug_type),
              plug_type: "",
              formation: "",
              display_name: p.plug_type ?? "Cement Plug",
              top_ft: p.depth_top_ft !== undefined ? Number(p.depth_top_ft) : "" as number | "",
              bottom_ft: p.depth_bottom_ft !== undefined ? Number(p.depth_bottom_ft) : "" as number | "",
              sacks: p.sacks !== undefined ? Number(p.sacks) : "" as number | "",
              cement_class: p.cement_class ?? "",
              tagged_depth_ft: "" as number | "",
              placement_method: p.method ?? "",
              woc_hours: "" as number | "",
              through_casing: "",
            }))
          )
        } else if (diagramType === "planned") {
          setStepRows(
            summary.plug_records.map((p) => ({
              id: uid(),
              type: mapPlugType(p.plug_type),
              plug_type: "",
              formation: "",
              top_ft: p.depth_top_ft !== undefined ? Number(p.depth_top_ft) : "" as number | "",
              bottom_ft: p.depth_bottom_ft !== undefined ? Number(p.depth_bottom_ft) : "" as number | "",
              sacks: p.sacks !== undefined ? Number(p.sacks) : "" as number | "",
              cement_class: p.cement_class ?? "",
              display_name: p.plug_type ?? "Cement Plug",
              plug_purpose: "isolation",
              through_casing: "",
            }))
          )
        }
      }

      success("Well data loaded.")
    } catch (err) {
      showError("Failed to load well data: " + ((err as Error)?.message ?? "unknown error"))
    } finally {
      setLoadingWell(false)
    }
  }, [wellHeader.api14, diagramType, success, showError])

  // ── Auto-populate when creating from WellDetail page with ?api14= ───────
  useEffect(() => {
    if (isEditMode || !urlApi14) return
    // Set the api14 in header first, then load
    setWellHeader(prev => ({ ...prev, api14: urlApi14 }))
    handleLoadFromWell(urlApi14)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Build diagram data (memoised) ───────────────────────────────────────
  const buildDiagramData = useMemo(() => {
    const well = { ...wellHeader }
    const well_geometry = {
      casing_strings: casingRows
        .filter((r) => r.bottom_ft !== "")
        .map((r) => ({
          string: r.string,
          top_ft: Number(r.top_ft) || 0,
          size_in: Number(r.size_in) || 0,
          bottom_ft: Number(r.bottom_ft) || 0,
          hole_size_in: Number(r.hole_size_in) || 0,
          cement_top_ft: r.cement_top_ft !== "" ? Number(r.cement_top_ft) : null,
        })),
      formation_tops: formationRows
        .filter((r) => r.top_ft !== "")
        .map((r) => ({
          formation: r.formation,
          top_ft: Number(r.top_ft),
          base_ft: r.base_ft !== "" ? Number(r.base_ft) : null,
        })),
      production_perforations: perfRows
        .filter((r) => r.top_ft !== "" && r.bottom_ft !== "")
        .map((r) => ({
          top_ft: Number(r.top_ft),
          bottom_ft: Number(r.bottom_ft),
        })),
      tubing: tubingRows
        .filter((r) => r.bottom_ft !== "")
        .map((r) => ({
          top_ft: r.top_ft !== "" ? Number(r.top_ft) : 0,
          size_in: Number(r.size_in) || 0,
          bottom_ft: Number(r.bottom_ft) || 0,
        })),
      existing_tools: toolRows
        .filter((r) => r.depth_ft !== "")
        .map((r) => ({
          tool_type: r.tool_type,
          depth_ft: Number(r.depth_ft),
          top_ft: r.top_ft !== "" ? Number(r.top_ft) : undefined,
          bottom_ft: r.bottom_ft !== "" ? Number(r.bottom_ft) : undefined,
        })),
      historic_cement_jobs: cementJobRows
        .filter((r) => r.measured_bottom_ft !== "")
        .map((r) => ({
          job_type: r.job_type,
          // Map new field names to renderer's expected field names
          interval_top_ft: r.measured_top_ft !== "" ? Number(r.measured_top_ft) : undefined,
          interval_bottom_ft: Number(r.measured_bottom_ft),
          cement_top_ft: r.tagged_top_ft !== "" ? Number(r.tagged_top_ft) : undefined,
          sacks: r.sacks !== "" ? Number(r.sacks) : undefined,
          through_casing: r.through_casing || undefined,
          is_new: r.is_new,
        })),
    }

    if (diagramType === "current") {
      return { well, well_geometry, payload: { steps: [] } }
    }

    if (diagramType === "planned") {
      return {
        well,
        well_geometry,
        payload: {
          steps: stepRows
            .filter((r) => r.bottom_ft !== "")
            .map((r) => {
              const isAnnular =
                r.plug_type === "perf_and_squeeze_plug" ||
                r.plug_type === "circulation_plug"
              const matchingCasing =
                isAnnular && r.through_casing
                  ? casingRows.find((c) => c.string === r.through_casing)
                  : null

              return {
                type: r.type,
                plug_type: r.plug_type || null,
                formation: r.formation,
                top_ft: r.top_ft !== "" ? Number(r.top_ft) : null,
                bottom_ft: Number(r.bottom_ft),
                sacks: r.sacks !== "" ? Number(r.sacks) : null,
                display_name: r.display_name,
                plug_purpose: r.plug_purpose || null,
                recipe: r.cement_class ? { class: r.cement_class } : undefined,
                through_casing: r.through_casing || undefined,
                ...(matchingCasing
                  ? {
                      details: {
                        annuli: [
                          {
                            inner_od_in: Number(matchingCasing.size_in),
                            outer_id_in:
                              matchingCasing.hole_size_in !== ""
                                ? Number(matchingCasing.hole_size_in)
                                : Number(matchingCasing.size_in) + 2.5,
                          },
                        ],
                      },
                    }
                  : {}),
              }
            }),
        },
      }
    }

    // as_plugged
    return {
      well,
      well_geometry,
      plugs: plugRows
        .filter((r) => r.bottom_ft !== "")
        .map((r) => {
          const isAnnular =
            r.plug_type === "perf_and_squeeze_plug" ||
            r.plug_type === "circulation_plug"
          const matchingCasing =
            isAnnular && r.through_casing
              ? casingRows.find((c) => c.string === r.through_casing)
              : null

          return {
            plug_number: Number(r.plug_number) || 1,
            type: r.type,
            plug_type: r.plug_type || null,
            formation: r.formation || null,
            display_name: r.display_name || null,
            top_ft: Number(r.top_ft) || 0,
            bottom_ft: Number(r.bottom_ft) || 0,
            sacks: r.sacks !== "" ? Number(r.sacks) : 0,
            cement_class: r.cement_class,
            tagged_depth_ft:
              r.tagged_depth_ft !== "" ? Number(r.tagged_depth_ft) : undefined,
            placement_method: r.placement_method,
            woc_hours: r.woc_hours !== "" ? Number(r.woc_hours) : undefined,
            through_casing: r.through_casing || undefined,
            ...(matchingCasing
              ? {
                  details: {
                    annuli: [
                      {
                        inner_od_in: Number(matchingCasing.size_in),
                        outer_id_in:
                          matchingCasing.hole_size_in !== ""
                            ? Number(matchingCasing.hole_size_in)
                            : Number(matchingCasing.size_in) + 2.5,
                      },
                    ],
                  },
                }
              : {}),
          }
        }),
      jurisdiction,
    }
  }, [
    diagramType,
    wellHeader,
    casingRows,
    formationRows,
    perfRows,
    tubingRows,
    toolRows,
    cementJobRows,
    stepRows,
    plugRows,
    jurisdiction,
  ])

  // ── renderData: transform buildDiagramData for SVG renderer ─────────────
  // buildDiagramData stores ALL cement jobs (with is_new flag) for persistence.
  // renderData filters: existing (is_new=false) stay in historic_cement_jobs,
  // new (is_new=true) are converted to steps/plugs so they render as new cement.
  const renderData = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data: any = { ...buildDiagramData }
    const geom = { ...(data.well_geometry || {}) }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const allCementJobs: any[] = geom.historic_cement_jobs || []
    // Existing cement stays in historic_cement_jobs (renderer reads this)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    geom.historic_cement_jobs = allCementJobs.filter((j: any) => !j.is_new)

    // New cement jobs become steps so renderer draws them as new cement (green)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const newCementSteps = allCementJobs
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .filter((j: any) => j.is_new)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((j: any) => {
        const matchingCasing = j.through_casing
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ? (geom.casing_strings || []).find((c: any) => c.string === j.through_casing)
          : null
        return {
          type: "cement_plug",
          plug_type: j.job_type === "perf_and_squeeze" ? "perf_and_squeeze_plug" :
                     j.job_type === "perf_and_circulate" ? "perf_and_circulate_plug" : "spot_plug",
          formation: "",
          top_ft: j.interval_top_ft ?? null,
          bottom_ft: j.interval_bottom_ft ?? null,
          sacks: j.sacks ?? null,
          display_name: j.job_type === "perf_and_squeeze" ? "P&S" :
                        j.job_type === "perf_and_circulate" ? "P&C" : "Spot",
          ...(matchingCasing ? {
            details: {
              annuli: [{
                inner_od_in: matchingCasing.size_in,
                outer_id_in: matchingCasing.hole_size_in || matchingCasing.size_in + 2.5,
              }]
            }
          } : {}),
        }
      })

    data.well_geometry = geom

    // Merge new cement steps into the diagram payload
    if (newCementSteps.length > 0) {
      if (data.payload !== undefined) {
        data.payload = { ...data.payload, steps: [...(data.payload?.steps || []), ...newCementSteps] }
      }
      // For as_plugged, also add as plugs
      if (data.plugs !== undefined) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const newPlugs = newCementSteps.map((s: any, i: number) => ({
          plug_number: (data.plugs?.length || 0) + i + 1,
          type: s.type,
          plug_type: s.plug_type,
          formation: s.formation,
          display_name: s.display_name,
          top_ft: s.top_ft || 0,
          bottom_ft: s.bottom_ft || 0,
          sacks: s.sacks || 0,
          details: s.details,
        }))
        data.plugs = [...data.plugs, ...newPlugs]
      }
    }

    return data
  }, [buildDiagramData])

  // ── Save ────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!wellHeader.api14) {
      showError("API14 is required.")
      return
    }
    setSaving(true)
    try {
      if (isEditMode && id) {
        await updateManualWBD(id, { title, diagram_data: buildDiagramData })
      } else {
        await createManualWBD({
          api14: wellHeader.api14,
          diagram_type: diagramType,
          title,
          diagram_data: buildDiagramData,
        })
      }
      success("Diagram saved.")
      navigate("/manual-wbd")
    } catch (err) {
      showError("Save failed: " + ((err as Error)?.message ?? "unknown"))
    } finally {
      setSaving(false)
    }
  }

  // ── Export PDF ──────────────────────────────────────────────────────────
  const handleExportPDF = async () => {
    const svgEl =
      diagramType === "as_plugged"
        ? asPluggedRef.current?.getSvgElement()
        : proposedRef.current?.getSvgElement()

    if (!svgEl) {
      showError("No diagram to export.")
      return
    }

    try {
      const doc = new jsPDF("portrait", "mm", "letter")
      const pageWidth = doc.internal.pageSize.getWidth()
      let y = 15

      doc.setFontSize(14)
      doc.setFont("helvetica", "bold")
      doc.text(title || "Wellbore Diagram", pageWidth / 2, y, { align: "center" })
      y += 8

      doc.setFontSize(10)
      doc.setFont("helvetica", "normal")
      doc.text(`API: ${wellHeader.api14}`, 15, y)
      doc.text(`Operator: ${wellHeader.operator_name}`, pageWidth / 2, y)
      y += 5
      doc.text(
        `Lease: ${wellHeader.lease_name}  Well #: ${wellHeader.well_number}`,
        15,
        y
      )
      doc.text(`Field: ${wellHeader.field_name}`, pageWidth / 2, y)

      doc.addPage()
      const pngBlob = await captureSvgAsPng(svgEl, 2)
      const dataUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result as string)
        reader.readAsDataURL(pngBlob)
      })
      const imgWidth = pageWidth - 20
      const bbox = svgEl.getBoundingClientRect()
      const svgAspect =
        bbox.height > 0 && bbox.width > 0 ? bbox.height / bbox.width : 1.5
      const imgHeight = imgWidth * svgAspect
      const pageHeight = doc.internal.pageSize.getHeight()
      doc.addImage(
        dataUrl,
        "PNG",
        10,
        10,
        imgWidth,
        Math.min(imgHeight, pageHeight - 20)
      )

      doc.save(`WBD_${wellHeader.api14 || "diagram"}.pdf`)
    } catch (err) {
      console.error("Export failed:", err)
      showError("PDF export failed.")
    }
  }

  // ── Row helpers ─────────────────────────────────────────────────────────
  function updateRow<T extends { id: string }>(
    setter: React.Dispatch<React.SetStateAction<T[]>>,
    rowId: string,
    patch: Partial<T>
  ) {
    setter((rows) => rows.map((r) => (r.id === rowId ? { ...r, ...patch } : r)))
  }

  function removeRow<T extends { id: string }>(
    setter: React.Dispatch<React.SetStateAction<T[]>>,
    rowId: string
  ) {
    setter((rows) => rows.filter((r) => r.id !== rowId))
  }

  // ── Visibility toggle ───────────────────────────────────────────────────
  const toggleVisibility = (key: keyof typeof visibility) => {
    setVisibility((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  // ── Render ──────────────────────────────────────────────────────────────
  if (loadingRecord) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
      </div>
    )
  }

  return (
    <>
      <PageMeta
        title={isEditMode ? "Edit WBD" : "New Manual WBD"}
        description="Manual Wellbore Diagram Editor"
      />
      <PageBreadcrumb pageTitle={isEditMode ? "Edit WBD" : "New Manual WBD"} />

      <div className="flex gap-4 min-h-screen">
        {/* ─── LEFT PANEL (form, scrollable) ─────────────────────── */}
        <div className="w-[55%] flex-shrink-0 pb-10">

          {/* ── Title & Diagram Type ── */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-4 mb-4">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
              {isEditMode ? "Edit Manual WBD" : "New Manual WBD"}
            </h2>

            {/* Diagram title */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Diagram Title
              </label>
              <Inp
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Current State WBD – XYZ #12H"
              />
            </div>

            {/* Diagram type selector */}
            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Diagram Type
              </label>
              <div className="flex gap-5">
                {(["current", "planned", "as_plugged"] as DiagramType[]).map((dt) => (
                  <label key={dt} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="diagram_type"
                      value={dt}
                      checked={diagramType === dt}
                      onChange={() => setDiagramType(dt)}
                      className="accent-brand-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">
                      {dt.replace("_", " ")}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Jurisdiction — only for as_plugged */}
            {diagramType === "as_plugged" && (
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Jurisdiction
                </label>
                <div className="flex gap-6">
                  {(["TX", "NM"] as Jurisdiction[]).map((j) => (
                    <label key={j} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="jurisdiction"
                        value={j}
                        checked={jurisdiction === j}
                        onChange={() => setJurisdiction(j)}
                        className="accent-brand-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{j}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Well Header ── */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-gray-800 dark:text-white">
                Well Header
              </h3>
              <button
                type="button"
                onClick={() => handleLoadFromWell()}
                disabled={loadingWell}
                className="px-3 py-1 text-xs rounded-lg border border-brand-500 text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors disabled:opacity-50"
              >
                {loadingWell ? "Loading…" : "Load from Well"}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  API14 *
                </label>
                <Inp
                  type="text"
                  value={wellHeader.api14}
                  onChange={(e) =>
                    setWellHeader((h) => ({ ...h, api14: e.target.value }))
                  }
                  placeholder="42-XXX-XXXXX-0000"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Operator
                </label>
                <Inp
                  type="text"
                  value={wellHeader.operator_name}
                  onChange={(e) =>
                    setWellHeader((h) => ({ ...h, operator_name: e.target.value }))
                  }
                  placeholder="Operator name"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Lease Name
                </label>
                <Inp
                  type="text"
                  value={wellHeader.lease_name}
                  onChange={(e) =>
                    setWellHeader((h) => ({ ...h, lease_name: e.target.value }))
                  }
                  placeholder="Lease"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Well Number
                </label>
                <Inp
                  type="text"
                  value={wellHeader.well_number}
                  onChange={(e) =>
                    setWellHeader((h) => ({ ...h, well_number: e.target.value }))
                  }
                  placeholder="1H"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Field Name
                </label>
                <Inp
                  type="text"
                  value={wellHeader.field_name}
                  onChange={(e) =>
                    setWellHeader((h) => ({ ...h, field_name: e.target.value }))
                  }
                  placeholder="Field"
                />
              </div>
            </div>
          </div>

          {/* ── Casing Strings ── */}
          <Section
            title="Casing Strings"
            onAdd={() => setCasingRows((r) => [...r, emptyCasing()])}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <TH>String</TH>
                    <TH>Size (in)</TH>
                    <TH>Top (ft)</TH>
                    <TH>Bottom (ft)</TH>
                    <TH>Hole Size (in)</TH>
                    <TH>Cement Top (ft)</TH>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {casingRows.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b border-gray-100 dark:border-gray-700/50"
                    >
                      <td className="py-1 pr-2">
                        <Sel
                          value={row.string}
                          onChange={(e) =>
                            updateRow(setCasingRows, row.id, { string: e.target.value })
                          }
                        >
                          <option>Surface</option>
                          <option>Intermediate</option>
                          <option>Production</option>
                          <option>Liner</option>
                          <option>Conductor</option>
                        </Sel>
                      </td>
                      <td className="py-1 pr-2">
                        <Inp
                          type="number"
                          value={row.size_in}
                          onChange={(e) =>
                            updateRow(setCasingRows, row.id, {
                              size_in: e.target.value === "" ? "" : Number(e.target.value),
                            })
                          }
                          placeholder="9.625"
                          step="0.001"
                        />
                      </td>
                      <td className="py-1 pr-2">
                        <Inp
                          type="number"
                          value={row.top_ft}
                          onChange={(e) =>
                            updateRow(setCasingRows, row.id, {
                              top_ft: e.target.value === "" ? "" : Number(e.target.value),
                            })
                          }
                          placeholder="0"
                        />
                      </td>
                      <td className="py-1 pr-2">
                        <Inp
                          type="number"
                          value={row.bottom_ft}
                          onChange={(e) =>
                            updateRow(setCasingRows, row.id, {
                              bottom_ft:
                                e.target.value === "" ? "" : Number(e.target.value),
                            })
                          }
                          placeholder="5000"
                        />
                      </td>
                      <td className="py-1 pr-2">
                        <Inp
                          type="number"
                          value={row.hole_size_in}
                          onChange={(e) =>
                            updateRow(setCasingRows, row.id, {
                              hole_size_in:
                                e.target.value === "" ? "" : Number(e.target.value),
                            })
                          }
                          placeholder="12.25"
                          step="0.001"
                        />
                      </td>
                      <td className="py-1 pr-2">
                        <Inp
                          type="number"
                          value={row.cement_top_ft}
                          onChange={(e) =>
                            updateRow(setCasingRows, row.id, {
                              cement_top_ft:
                                e.target.value === "" ? "" : Number(e.target.value),
                            })
                          }
                          placeholder="—"
                        />
                      </td>
                      <td className="py-1">
                        <RemoveBtn onClick={() => removeRow(setCasingRows, row.id)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* ── Formation Tops ── */}
          <Section
            title="Formation Tops"
            onAdd={() => setFormationRows((r) => [...r, emptyFormation()])}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <TH>Formation</TH>
                    <TH>Top (ft)</TH>
                    <TH>Base (ft)</TH>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {formationRows.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b border-gray-100 dark:border-gray-700/50"
                    >
                      <td className="py-1 pr-2">
                        <Inp
                          type="text"
                          value={row.formation}
                          onChange={(e) =>
                            updateRow(setFormationRows, row.id, {
                              formation: e.target.value,
                            })
                          }
                          placeholder="Wolfcamp A"
                        />
                      </td>
                      <td className="py-1 pr-2">
                        <Inp
                          type="number"
                          value={row.top_ft}
                          onChange={(e) =>
                            updateRow(setFormationRows, row.id, {
                              top_ft: e.target.value === "" ? "" : Number(e.target.value),
                            })
                          }
                          placeholder="7000"
                        />
                      </td>
                      <td className="py-1 pr-2">
                        <Inp
                          type="number"
                          value={row.base_ft}
                          onChange={(e) =>
                            updateRow(setFormationRows, row.id, {
                              base_ft: e.target.value === "" ? "" : Number(e.target.value),
                            })
                          }
                          placeholder="7500"
                        />
                      </td>
                      <td className="py-1">
                        <RemoveBtn onClick={() => removeRow(setFormationRows, row.id)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* ── Perforations ── */}
          <Section
            title="Production Perforations"
            onAdd={() => setPerfRows((r) => [...r, emptyPerf()])}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <TH>Top (ft)</TH>
                    <TH>Bottom (ft)</TH>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {perfRows.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b border-gray-100 dark:border-gray-700/50"
                    >
                      <td className="py-1 pr-2">
                        <Inp
                          type="number"
                          value={row.top_ft}
                          onChange={(e) =>
                            updateRow(setPerfRows, row.id, {
                              top_ft: e.target.value === "" ? "" : Number(e.target.value),
                            })
                          }
                          placeholder="9800"
                        />
                      </td>
                      <td className="py-1 pr-2">
                        <Inp
                          type="number"
                          value={row.bottom_ft}
                          onChange={(e) =>
                            updateRow(setPerfRows, row.id, {
                              bottom_ft:
                                e.target.value === "" ? "" : Number(e.target.value),
                            })
                          }
                          placeholder="9900"
                        />
                      </td>
                      <td className="py-1">
                        <RemoveBtn onClick={() => removeRow(setPerfRows, row.id)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* ── Tubing ── */}
          <Section
            title="Tubing"
            onAdd={() => setTubingRows((r) => [...r, emptyTubing()])}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <TH>Size (in)</TH>
                    <TH>Top (ft)</TH>
                    <TH>Bottom (ft)</TH>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {tubingRows.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b border-gray-100 dark:border-gray-700/50"
                    >
                      <td className="py-1 pr-2">
                        <Inp
                          type="number"
                          value={row.size_in}
                          onChange={(e) =>
                            updateRow(setTubingRows, row.id, {
                              size_in: e.target.value === "" ? "" : Number(e.target.value),
                            })
                          }
                          placeholder="2.375"
                          step="0.001"
                        />
                      </td>
                      <td className="py-1 pr-2">
                        <Inp
                          type="number"
                          value={row.top_ft}
                          onChange={(e) =>
                            updateRow(setTubingRows, row.id, {
                              top_ft: e.target.value === "" ? "" : Number(e.target.value),
                            })
                          }
                          placeholder="0"
                        />
                      </td>
                      <td className="py-1 pr-2">
                        <Inp
                          type="number"
                          value={row.bottom_ft}
                          onChange={(e) =>
                            updateRow(setTubingRows, row.id, {
                              bottom_ft:
                                e.target.value === "" ? "" : Number(e.target.value),
                            })
                          }
                          placeholder="8000"
                        />
                      </td>
                      <td className="py-1">
                        <RemoveBtn onClick={() => removeRow(setTubingRows, row.id)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* ── Existing Tools ── */}
          <Section
            title="Existing Tools"
            onAdd={() => setToolRows((r) => [...r, emptyTool()])}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <TH>Tool Type</TH>
                    <TH>Depth (ft)</TH>
                    <TH>Top (ft)</TH>
                    <TH>Bottom (ft)</TH>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {toolRows.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="py-3 text-center text-gray-400 dark:text-gray-500 italic"
                      >
                        No tools — click "+ Add Row" to add one
                      </td>
                    </tr>
                  )}
                  {toolRows.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b border-gray-100 dark:border-gray-700/50"
                    >
                      <td className="py-1 pr-2">
                        <Sel
                          value={row.tool_type}
                          onChange={(e) =>
                            updateRow(setToolRows, row.id, {
                              tool_type: e.target.value,
                            })
                          }
                        >
                          <option value="CIBP">CIBP</option>
                          <option value="Packer">Packer</option>
                          <option value="Retainer">Retainer</option>
                          <option value="DV Tool">DV Tool</option>
                          <option value="Straddle Packer">Straddle Packer</option>
                          <option value="Bridge Plug">Bridge Plug</option>
                          <option value="Other">Other</option>
                        </Sel>
                      </td>
                      <td className="py-1 pr-2">
                        <Inp
                          type="number"
                          value={row.depth_ft}
                          onChange={(e) =>
                            updateRow(setToolRows, row.id, {
                              depth_ft:
                                e.target.value === "" ? "" : Number(e.target.value),
                            })
                          }
                          placeholder="5000"
                        />
                      </td>
                      <td className="py-1 pr-2">
                        <Inp
                          type="number"
                          value={row.top_ft}
                          onChange={(e) =>
                            updateRow(setToolRows, row.id, {
                              top_ft: e.target.value === "" ? "" : Number(e.target.value),
                            })
                          }
                          placeholder="—"
                        />
                      </td>
                      <td className="py-1 pr-2">
                        <Inp
                          type="number"
                          value={row.bottom_ft}
                          onChange={(e) =>
                            updateRow(setToolRows, row.id, {
                              bottom_ft:
                                e.target.value === "" ? "" : Number(e.target.value),
                            })
                          }
                          placeholder="—"
                        />
                      </td>
                      <td className="py-1">
                        <RemoveBtn onClick={() => removeRow(setToolRows, row.id)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* ── Historic Cement Jobs ── */}
          <Section
            title="Historic Cement Jobs"
            onAdd={() => setCementJobRows((r) => [...r, emptyCementJob()])}
            addLabel="Add Cement Job"
          >
            <div className="overflow-x-auto">
              {cementJobRows.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500 italic">
                  No historic cement jobs added.
                </p>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <TH>Job Type</TH>
                      <TH>Meas. Top (ft)</TH>
                      <TH>Meas. Bottom (ft)</TH>
                      <TH>Tagged Top (ft)</TH>
                      <TH>Sacks</TH>
                      <TH>Through Casing</TH>
                      <th className="pb-2 pr-2 text-left text-gray-500 dark:text-gray-400 font-medium">Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {cementJobRows.map((row) => (
                      <tr
                        key={row.id}
                        className="border-b border-gray-100 dark:border-gray-700/50"
                      >
                        <td className="py-1 pr-2">
                          <Sel
                            value={row.job_type}
                            onChange={(e) =>
                              updateRow(setCementJobRows, row.id, { job_type: e.target.value })
                            }
                          >
                            <option value="perf_and_squeeze">Perf &amp; Squeeze</option>
                            <option value="perf_and_circulate">Perf &amp; Circulate</option>
                            <option value="spot_and_dump_bail">Spot &amp; Dump Bail</option>
                          </Sel>
                        </td>
                        <td className="py-1 pr-2">
                          <Inp
                            type="number"
                            value={row.measured_top_ft}
                            onChange={(e) =>
                              updateRow(setCementJobRows, row.id, {
                                measured_top_ft:
                                  e.target.value === "" ? "" : Number(e.target.value),
                              })
                            }
                            placeholder="Top"
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <Inp
                            type="number"
                            value={row.measured_bottom_ft}
                            onChange={(e) =>
                              updateRow(setCementJobRows, row.id, {
                                measured_bottom_ft:
                                  e.target.value === "" ? "" : Number(e.target.value),
                              })
                            }
                            placeholder="Bottom"
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <Inp
                            type="number"
                            value={row.tagged_top_ft}
                            onChange={(e) =>
                              updateRow(setCementJobRows, row.id, {
                                tagged_top_ft:
                                  e.target.value === "" ? "" : Number(e.target.value),
                              })
                            }
                            placeholder="Tagged top"
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <Inp
                            type="number"
                            value={row.sacks}
                            onChange={(e) =>
                              updateRow(setCementJobRows, row.id, {
                                sacks:
                                  e.target.value === "" ? "" : Number(e.target.value),
                              })
                            }
                            placeholder="Sacks"
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <Sel
                            value={row.through_casing}
                            onChange={(e) =>
                              updateRow(setCementJobRows, row.id, {
                                through_casing: e.target.value,
                              })
                            }
                          >
                            <option value="">— Select Casing —</option>
                            {casingRows
                              .filter((c) => c.string && c.size_in !== "")
                              .map((c) => (
                                <option key={c.id} value={c.string}>
                                  {c.string} ({c.size_in}")
                                </option>
                              ))}
                          </Sel>
                        </td>
                        <td className="py-1 px-1">
                          <button
                            type="button"
                            onClick={() => updateRow(setCementJobRows, row.id, { is_new: !row.is_new })}
                            className={`px-2 py-0.5 rounded text-xs font-medium ${
                              row.is_new
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                            }`}
                          >
                            {row.is_new ? 'New' : 'Existing'}
                          </button>
                        </td>
                        <td className="py-1">
                          <RemoveBtn onClick={() => removeRow(setCementJobRows, row.id)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Section>

          {/* ── Planned Steps (planned type only) ── */}
          {diagramType === "planned" && (
            <Section
              title="Plug Steps (Planned)"
              onAdd={() => setStepRows((r) => [...r, emptyStep()])}
            >
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <TH>Type</TH>
                      <TH>Plug Type</TH>
                      <TH>Purpose</TH>
                      <TH>Formation</TH>
                      <TH>Display Name</TH>
                      <TH>Top (ft)</TH>
                      <TH>Bottom (ft)</TH>
                      <TH>Sacks</TH>
                      <TH>Class</TH>
                      <TH>Through Casing</TH>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {stepRows.map((row) => (
                      <tr
                        key={row.id}
                        className="border-b border-gray-100 dark:border-gray-700/50"
                      >
                        <td className="py-1 pr-2">
                          <Sel
                            value={row.type}
                            onChange={(e) =>
                              updateRow(setStepRows, row.id, { type: e.target.value })
                            }
                          >
                            <option value="cement_plug">Cement Plug</option>
                            <option value="bridge_plug">Bridge Plug</option>
                            <option value="surface_plug">Surface Plug</option>
                            <option value="formation_plug">Formation Plug</option>
                            <option value="perf_and_squeeze">Perf &amp; Squeeze</option>
                            <option value="perf_and_circulate">Perf &amp; Circulate</option>
                            <option value="balanced_plug">Balanced Plug</option>
                            <option value="cibp">CIBP</option>
                          </Sel>
                        </td>
                        <td className="py-1 pr-2">
                          <Sel
                            value={row.plug_type}
                            onChange={(e) =>
                              updateRow(setStepRows, row.id, { plug_type: e.target.value })
                            }
                          >
                            <option value="">—</option>
                            <option value="spot_plug">Spot Plug</option>
                            <option value="perf_and_squeeze_plug">Perf &amp; Squeeze</option>
                            <option value="balanced_plug">Balanced Plug</option>
                            <option value="circulation_plug">Circulation Plug</option>
                          </Sel>
                        </td>
                        <td className="py-1 pr-2">
                          <Sel
                            value={row.plug_purpose}
                            onChange={(e) =>
                              updateRow(setStepRows, row.id, { plug_purpose: e.target.value })
                            }
                          >
                            <option value="">—</option>
                            <option value="isolation">Isolation</option>
                            <option value="abandonment">Abandonment</option>
                            <option value="protection">Protection</option>
                          </Sel>
                        </td>
                        <td className="py-1 pr-2">
                          <Inp
                            type="text"
                            value={row.formation}
                            onChange={(e) =>
                              updateRow(setStepRows, row.id, {
                                formation: e.target.value,
                              })
                            }
                            placeholder="Wolfcamp"
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <Inp
                            type="text"
                            value={row.display_name}
                            onChange={(e) =>
                              updateRow(setStepRows, row.id, {
                                display_name: e.target.value,
                              })
                            }
                            placeholder="Plug 1"
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <Inp
                            type="number"
                            value={row.top_ft}
                            onChange={(e) =>
                              updateRow(setStepRows, row.id, {
                                top_ft:
                                  e.target.value === "" ? "" : Number(e.target.value),
                              })
                            }
                            placeholder="9500"
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <Inp
                            type="number"
                            value={row.bottom_ft}
                            onChange={(e) =>
                              updateRow(setStepRows, row.id, {
                                bottom_ft:
                                  e.target.value === "" ? "" : Number(e.target.value),
                              })
                            }
                            placeholder="9800"
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <Inp
                            type="number"
                            value={row.sacks}
                            onChange={(e) =>
                              updateRow(setStepRows, row.id, {
                                sacks:
                                  e.target.value === "" ? "" : Number(e.target.value),
                              })
                            }
                            placeholder="50"
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <Sel
                            value={row.cement_class}
                            onChange={(e) =>
                              updateRow(setStepRows, row.id, {
                                cement_class: e.target.value,
                              })
                            }
                          >
                            <option value="">—</option>
                            <option value="A">A</option>
                            <option value="C">C</option>
                            <option value="G">G</option>
                            <option value="H">H</option>
                          </Sel>
                        </td>
                        <td className="py-1 pr-2">
                          {(row.plug_type === "perf_and_squeeze_plug" ||
                            row.plug_type === "circulation_plug") ? (
                            <Sel
                              value={row.through_casing}
                              onChange={(e) =>
                                updateRow(setStepRows, row.id, {
                                  through_casing: e.target.value,
                                })
                              }
                            >
                              <option value="">— Select —</option>
                              {casingRows
                                .filter((c) => c.string && c.size_in !== "")
                                .map((c) => (
                                  <option key={c.id} value={c.string}>
                                    {c.string} ({c.size_in}")
                                  </option>
                                ))}
                            </Sel>
                          ) : (
                            <span className="text-gray-300 dark:text-gray-600 text-xs">N/A</span>
                          )}
                        </td>
                        <td className="py-1">
                          <RemoveBtn onClick={() => removeRow(setStepRows, row.id)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          {/* ── As-Plugged Plugs (as_plugged type only) ── */}
          {diagramType === "as_plugged" && (
            <Section
              title="Plugs (As-Plugged)"
              onAdd={() =>
                setPlugRows((r) => [...r, emptyPlug(r.length + 1)])
              }
            >
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <TH>#</TH>
                      <TH>Type</TH>
                      <TH>Plug Type</TH>
                      <TH>Formation</TH>
                      <TH>Display Name</TH>
                      <TH>Top (ft)</TH>
                      <TH>Bottom (ft)</TH>
                      <TH>Sacks</TH>
                      <TH>Class</TH>
                      <TH>Tagged (ft)</TH>
                      <TH>Method</TH>
                      <TH>WOC (hrs)</TH>
                      <TH>Through Casing</TH>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {plugRows.map((row) => (
                      <tr
                        key={row.id}
                        className="border-b border-gray-100 dark:border-gray-700/50"
                      >
                        <td className="py-1 pr-2">
                          <Inp
                            type="number"
                            value={row.plug_number}
                            onChange={(e) =>
                              updateRow(setPlugRows, row.id, {
                                plug_number:
                                  e.target.value === "" ? "" : Number(e.target.value),
                              })
                            }
                            placeholder="1"
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <Sel
                            value={row.type}
                            onChange={(e) =>
                              updateRow(setPlugRows, row.id, { type: e.target.value })
                            }
                          >
                            <option value="cement_plug">Cement Plug</option>
                            <option value="bridge_plug">Bridge Plug</option>
                            <option value="surface_plug">Surface Plug</option>
                            <option value="formation_plug">Formation Plug</option>
                            <option value="perf_and_squeeze">Perf &amp; Squeeze</option>
                            <option value="perf_and_circulate">Perf &amp; Circulate</option>
                            <option value="balanced_plug">Balanced Plug</option>
                            <option value="cibp">CIBP</option>
                          </Sel>
                        </td>
                        <td className="py-1 pr-2">
                          <Sel
                            value={row.plug_type}
                            onChange={(e) =>
                              updateRow(setPlugRows, row.id, { plug_type: e.target.value })
                            }
                          >
                            <option value="">—</option>
                            <option value="spot_plug">Spot Plug</option>
                            <option value="perf_and_squeeze_plug">Perf &amp; Squeeze</option>
                            <option value="balanced_plug">Balanced Plug</option>
                            <option value="circulation_plug">Circulation Plug</option>
                          </Sel>
                        </td>
                        <td className="py-1 pr-2">
                          <Inp
                            type="text"
                            value={row.formation}
                            onChange={(e) =>
                              updateRow(setPlugRows, row.id, { formation: e.target.value })
                            }
                            placeholder="Formation"
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <Inp
                            type="text"
                            value={row.display_name}
                            onChange={(e) =>
                              updateRow(setPlugRows, row.id, { display_name: e.target.value })
                            }
                            placeholder="Plug 1"
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <Inp
                            type="number"
                            value={row.top_ft}
                            onChange={(e) =>
                              updateRow(setPlugRows, row.id, {
                                top_ft:
                                  e.target.value === "" ? "" : Number(e.target.value),
                              })
                            }
                            placeholder="9500"
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <Inp
                            type="number"
                            value={row.bottom_ft}
                            onChange={(e) =>
                              updateRow(setPlugRows, row.id, {
                                bottom_ft:
                                  e.target.value === "" ? "" : Number(e.target.value),
                              })
                            }
                            placeholder="9800"
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <Inp
                            type="number"
                            value={row.sacks}
                            onChange={(e) =>
                              updateRow(setPlugRows, row.id, {
                                sacks:
                                  e.target.value === "" ? "" : Number(e.target.value),
                              })
                            }
                            placeholder="50"
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <Sel
                            value={row.cement_class}
                            onChange={(e) =>
                              updateRow(setPlugRows, row.id, {
                                cement_class: e.target.value,
                              })
                            }
                          >
                            <option value="">—</option>
                            <option value="A">A</option>
                            <option value="C">C</option>
                            <option value="G">G</option>
                            <option value="H">H</option>
                          </Sel>
                        </td>
                        <td className="py-1 pr-2">
                          <Inp
                            type="number"
                            value={row.tagged_depth_ft}
                            onChange={(e) =>
                              updateRow(setPlugRows, row.id, {
                                tagged_depth_ft:
                                  e.target.value === "" ? "" : Number(e.target.value),
                              })
                            }
                            placeholder="—"
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <Sel
                            value={row.placement_method}
                            onChange={(e) =>
                              updateRow(setPlugRows, row.id, {
                                placement_method: e.target.value,
                              })
                            }
                          >
                            <option value="">—</option>
                            <option value="spot">Spot</option>
                            <option value="perf_and_squeeze">Perf &amp; Squeeze</option>
                            <option value="perf_and_circ">Perf &amp; Circulate</option>
                            <option value="dump_bailer">Dump Bailer</option>
                          </Sel>
                        </td>
                        <td className="py-1 pr-2">
                          <Inp
                            type="number"
                            value={row.woc_hours}
                            onChange={(e) =>
                              updateRow(setPlugRows, row.id, {
                                woc_hours:
                                  e.target.value === "" ? "" : Number(e.target.value),
                              })
                            }
                            placeholder="—"
                          />
                        </td>
                        <td className="py-1 pr-2">
                          {(row.plug_type === "perf_and_squeeze_plug" ||
                            row.plug_type === "circulation_plug") ? (
                            <Sel
                              value={row.through_casing}
                              onChange={(e) =>
                                updateRow(setPlugRows, row.id, {
                                  through_casing: e.target.value,
                                })
                              }
                            >
                              <option value="">— Select —</option>
                              {casingRows
                                .filter((c) => c.string && c.size_in !== "")
                                .map((c) => (
                                  <option key={c.id} value={c.string}>
                                    {c.string} ({c.size_in}")
                                  </option>
                                ))}
                            </Sel>
                          ) : (
                            <span className="text-gray-300 dark:text-gray-600 text-xs">N/A</span>
                          )}
                        </td>
                        <td className="py-1">
                          <RemoveBtn onClick={() => removeRow(setPlugRows, row.id)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          {/* ── Action Buttons ── */}
          <div className="flex gap-3 mt-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2 rounded-lg bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-50 transition-colors text-sm font-medium"
            >
              {saving ? "Saving…" : isEditMode ? "Save Changes" : "Save Diagram"}
            </button>
            <button
              type="button"
              onClick={handleExportPDF}
              className="px-5 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
            >
              Export PDF
            </button>
            <button
              type="button"
              onClick={() => navigate("/manual-wbd")}
              className="px-5 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* ─── RIGHT PANEL (sticky live preview) ─────────────── */}
        <div className="flex-1 min-w-0">
          <div className="sticky top-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-4">
              <h3 className="text-base font-semibold text-gray-800 dark:text-white mb-3">
                Live Preview
              </h3>

              {/* Diagram container */}
              <div className="overflow-auto max-h-[70vh] rounded-lg border border-gray-200 dark:border-gray-700 bg-white">
                {diagramType === "as_plugged" ? (
                  <AsPluggedWBD
                    ref={asPluggedRef}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    data={renderData as any}
                    visibility={visibility}
                  />
                ) : (
                  <ProposedPlugDiagram
                    ref={proposedRef}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    data={renderData as any}
                    visibility={visibility}
                  />
                )}
              </div>

              {/* Visibility toggles */}
              <div className="mt-4">
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">
                  Layer Visibility
                </p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  {(
                    Object.keys(visibility) as Array<keyof typeof visibility>
                  ).map((key) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={visibility[key]}
                        onChange={() => toggleVisibility(key)}
                        className="accent-brand-500 rounded"
                      />
                      <span className="text-xs text-gray-600 dark:text-gray-400 capitalize">
                        {key.replace(/([A-Z])/g, " $1")}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
